
import { X, Shield, Tag, IndianRupee, Clock, Star, CheckCircle, XCircle, Users, Building2, Percent, Package } from "lucide-react";

// ─── ATOMS ────────────────────────────────────────────────────────────────────
function InfoChip({ label, value, accent = "#2563EB", bg = "#EFF6FF" }) {
  return (
    <div className="flex flex-col gap-0.5 p-3 rounded-2xl" style={{ background: bg }}>
      <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: accent }}>
        {label}
      </span>
      <span className="text-[14px] font-black text-slate-800 font-mono leading-tight">
        {value ?? "—"}
      </span>
    </div>
  );
}

function SectionHeading({ icon: Icon, label, color = "#2563EB" }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: color + "18" }}>
        <Icon size={13} style={{ color }} />
      </div>
      <span className="text-[11px] font-black uppercase tracking-widest" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

function PayoutBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-[11px] font-semibold text-slate-500">{label}</span>
        <span className="text-[12px] font-black text-slate-800">₹{Number(value || 0).toLocaleString("en-IN")}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

function ListItem({ text, type = "check" }) {
  const isCheck = type === "check";
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <div
        className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: isCheck ? "#ECFDF5" : "#FEF2F2" }}
      >
        {isCheck
          ? <CheckCircle size={10} style={{ color: "#10B981" }} />
          : <XCircle size={10} style={{ color: "#EF4444" }} />
        }
      </div>
      <span className="text-[12px] text-slate-600 leading-snug">{text.trim()}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN MODAL
// ─────────────────────────────────────────────────────────────────────────────
export default function PriceTemplateViewModal({ isOpen, onClose, template }) {
  if (!isOpen || !template) return null;

  const product      = template.warranty_product;
  const coverageList = product?.coverage?.split(".").map(s => s.trim()).filter(Boolean) || [];
  const exclusionList= product?.exclusions?.split("🚫").map(s => s.trim()).filter(Boolean) || [];
  const featureList  = product?.features?.split(".").map(s => s.trim()).filter(Boolean) || [];

  const maxPayout = Math.max(
    Number(template.company_payout || 0),
    Number(template.retailer_payout || 0),
    Number(template.emp_payout || 0),
    Number(template.other_payout || 0),
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl"
        style={{
          background:  "#fff",
          boxShadow:   "0 32px 80px rgba(0,0,0,0.22)",
        }}
      >

        {/* ── HERO HEADER ─────────────────────────────────────────────────── */}
        <div
          className="relative rounded-t-3xl px-6 pt-6 pb-8 overflow-hidden"
          style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#2563EB 55%,#7C3AED 100%)" }}
        >
          {/* Blobs */}
          <div className="absolute -right-10 -top-10 w-36 h-36 rounded-full opacity-10" style={{ background: "white" }}/>
          <div className="absolute -left-6 bottom-0 w-24 h-24 rounded-full opacity-5" style={{ background: "white" }}/>

          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-xl flex items-center justify-center transition-colors z-10"
            style={{ background: "rgba(255,255,255,0.15)", color: "white" }}
          >
            <X size={15}/>
          </button>

          {/* Product image or icon */}
          <div className="flex items-start gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)" }}
            >
              {product?.image
                ? <img src={product.image} alt="" className="w-10 h-10 object-contain"/>
                : <Shield size={24} color="white"/>
              }
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span
                  className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.9)" }}
                >
                  {template.product_type || "Warranty"}
                </span>
                {product?.is_offer === 1 && (
                  <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ background: "#FEF3C7", color: "#D97706" }}>
                    Offer
                  </span>
                )}
              </div>
              <h2 className="text-white font-black text-[20px] leading-tight">
                {product?.name || "Warranty Product"}
              </h2>
              <p className="text-blue-200 text-[12px] mt-0.5">
                Template ID #{template.id} · {product?.validity} days validity · {product?.claims} claims
              </p>
            </div>
          </div>

          {/* Category pills */}
          {product?.categories?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {product.categories.map(cat => (
                <span
                  key={cat.id}
                  className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                  style={{ background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.85)" }}
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── BODY ─────────────────────────────────────────────────────────── */}
        <div className="px-6 py-5 space-y-6">

          {/* Price Range + MRP row */}
          <div>
            <SectionHeading icon={IndianRupee} label="Pricing Details" color="#2563EB"/>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <InfoChip label="Min Price"    value={`₹${Number(template.min_price||0).toLocaleString("en-IN")}`}  accent="#0891B2"  bg="#ECFEFF"/>
              <InfoChip label="Max Price"    value={`₹${Number(template.max_price||0).toLocaleString("en-IN")}`}  accent="#7C3AED"  bg="#F5F3FF"/>
              <InfoChip label="Product MRP"  value={`₹${Number(product?.mrp||0).toLocaleString("en-IN")}`}        accent="#2563EB"  bg="#EFF6FF"/>
              <InfoChip label="Device Price Up To"value={`₹${Number(template.product_price||0).toLocaleString("en-IN")}`} accent="#059669" bg="#ECFDF5"/>
            </div>
          </div>

          {/* Type flags */}
          <div className="flex flex-wrap gap-2">
            {[
              { label: template.is_fixed  === 1 ? "Fixed Price"   : "Variable Price",  color: "#2563EB", bg: "#EFF6FF" },
              // { label: template.is_percent=== 1 ? "% Based"       : "Flat Amount",     color: "#7C3AED", bg: "#F5F3FF" },
              { label: `HSN: ${product?.hsn_code || "—"}`,                              color: "#0891B2", bg: "#ECFEFF" },
            ].map(f => (
              <span key={f.label} className="text-[11px] font-bold px-3 py-1 rounded-full"
                style={{ background: f.bg, color: f.color, border: `1px solid ${f.color}22` }}>
                {f.label}
              </span>
            ))}
          </div>

          {/* Payout breakdown */}
         <div className="grid grid-cols-[70%_30%] gap-4">
            {/* LEFT CARD (70%) */}
            <div
              className="rounded-2xl p-4 space-y-3"
              style={{ background: "#F8FAFC", border: "1px solid #F1F5F9" }}
            >
              <SectionHeading icon={Users} label="Payout Breakdown" color="#6366F1"/>

              <PayoutBar label="Company Payout"  value={template.company_payout}  max={maxPayout} color="#2563EB"/>
              <PayoutBar label="Retailer Payout" value={template.retailer_payout} max={maxPayout} color="#7C3AED"/>
              <PayoutBar label="Employee Payout" value={template.emp_payout}      max={maxPayout} color="#10B981"/>
              <PayoutBar label="Other Payout"    value={template.other_payout}    max={maxPayout} color="#F59E0B"/>
            </div>

            {/* RIGHT CARD (30%) */}
            <div
              className="grid grid-rows-3 gap-3 rounded-2xl p-4"
              style={{
                background: "linear-gradient(135deg,#F0EFFF,#EDE8FF)",
                border: "1px solid #C4B5FD"
              }}
            >
              {/* Validity */}
              <div className="text-center">
                <p className="text-[10px] font-black uppercase tracking-widest text-purple-500 mb-0.5">
                  Validity
                </p>
                <p className="text-[18px] font-black text-slate-800">
                  {product?.validity || "—"}
                </p>
                <p className="text-[10px] text-slate-400">days</p>
              </div>

              {/* Claims */}
              <div className="text-center border-y" style={{ borderColor: "#C4B5FD" }}>
                <p className="text-[10px] font-black uppercase tracking-widest text-purple-500 mb-0.5">
                  Claims
                </p>
                <p className="text-[18px] font-black text-slate-800">
                  {product?.claims || "—"}
                </p>
                <p className="text-[10px] text-slate-400">allowed</p>
              </div>

              {/* Margin */}
              <div className="text-center">
                <p className="text-[10px] font-black uppercase tracking-widest text-purple-500 mb-0.5">
                  Margin
                </p>
                <p className="text-[18px] font-black text-slate-800">
                  ₹{product?.margin || "—"}
                </p>
                <p className="text-[10px] text-slate-400">per plan</p>
              </div>
            </div>

          </div>

          {/* Features */}
          {featureList.length > 0 && (
            <div>
              <SectionHeading icon={Star} label="Features" color="#D97706"/>
              <div
                className="rounded-2xl px-4 py-2 divide-y"
                style={{ background: "#FFFBEB", border: "1px solid #FEF3C7", divideColor: "#FEF3C7" }}
              >
                {featureList.map((f, i) => <ListItem key={i} text={f} type="check"/>)}
              </div>
            </div>
          )}

          {/* Coverage */}
          {coverageList.length > 0 ? (
            <div>
              <SectionHeading icon={CheckCircle} label="Coverage" color="#059669"/>
              <div
                className="rounded-2xl px-4 py-2 divide-y"
                style={{ background: "#ECFDF5", border: "1px solid #D1FAE5" }}
              >
                {coverageList.map((c, i) => <ListItem key={i} text={c} type="check"/>)}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-3 rounded-2xl"
              style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1" }}>
              <Package size={14} className="text-slate-300"/>
              <span className="text-[12px] text-slate-400">No coverage details specified</span>
            </div>
          )}

          {/* Exclusions */}
          {exclusionList.length > 0 && (
            <div>
              <SectionHeading icon={XCircle} label="Exclusions" color="#EF4444"/>
              <div
                className="rounded-2xl px-4 py-2 divide-y"
                style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
              >
                {exclusionList.map((e, i) => <ListItem key={i} text={e} type="cross"/>)}
              </div>
            </div>
          )}

          {/* Validity + Claims summary strip */}
          

          {/* Close button */}
          <button
            onClick={onClose}
            className="w-full py-3 rounded-2xl text-[13px] font-black text-white transition-all active:scale-95"
            style={{ background: "linear-gradient(135deg,#1E3A8A,#2563EB)", boxShadow: "0 6px 20px rgba(37,99,235,0.35)" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// import { X } from "lucide-react";

// export default function PriceTemplateViewModal({
//   isOpen,
//   onClose,
//   template,
// }) {
//   if (!isOpen || !template) return null;

//   const product = template.warranty_product;

//   const coverageList =
//     product?.coverage?.split(".").filter(Boolean) || [];

//   const exclusionList =
//     product?.exclusions?.split("🚫").filter(Boolean) || [];

//   return (
//     <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4">
//       <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl relative">

//         {/* ===== HEADER ===== */}
//         <div className="sticky top-0 bg-white rounded-t-3xl px-6 py-4 border-b border-slate-100 flex justify-between items-center">
//           <h2 className="text-lg font-semibold text-slate-900">
//             Price Template Details
//           </h2>
//           <button
//             onClick={onClose}
//             className="p-2 rounded-xl hover:bg-slate-100 transition"
//           >
//             <X size={18} />
//           </button>
//         </div>

//         {/* ===== BODY ===== */}
//         <div className="p-6 space-y-6">

//           {/* PRODUCT IMAGE */}
//           <div className="flex justify-center">
//             <img
//               src={product?.image}
//               alt={product?.name}
//               className="h-48 object-contain rounded-2xl border border-slate-200 bg-slate-50 p-4"
//             />
//           </div>

//           {/* PRODUCT TITLE */}
//           <div>
//             <h3 className="text-xl font-semibold text-slate-900">
//               {product?.name}
//             </h3>

//             <div className="flex flex-wrap gap-2 mt-2">
//               {product?.categories?.map((cat) => (
//                 <span
//                   key={cat.id}
//                   className="px-3 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full border border-blue-200"
//                 >
//                   {cat.name}
//                 </span>
//               ))}
//             </div>
//           </div>

//           {/* PRICE SECTION */}
//           <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 grid grid-cols-2 gap-4 text-sm">

//             <div>
//               <div className="text-slate-500 text-xs">Min Price</div>
//               <div className="font-semibold text-slate-900">
//                 ₹{template.min_price}
//               </div>
//             </div>

//             <div>
//               <div className="text-slate-500 text-xs">Max Price</div>
//               <div className="font-semibold text-slate-900">
//                 ₹{template.max_price}
//               </div>
//             </div>

//             <div>
//               <div className="text-slate-500 text-xs">Product Price</div>
//               <div className="font-semibold text-slate-900">
//                 ₹{template.product_price}
//               </div>
//             </div>

//             <div>
//               <div className="text-slate-500 text-xs">Company Payout</div>
//               <div className="font-semibold text-slate-900">
//                 ₹{template.company_payout}
//               </div>
//             </div>
//           </div>

//           {/* PAYOUT SECTION */}
//           <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 grid grid-cols-3 gap-4 text-sm">

//             <div>
//               <div className="text-indigo-500 text-xs">Employee</div>
//               <div className="font-semibold text-slate-900">
//                 ₹{template.emp_payout}
//               </div>
//             </div>

//             <div>
//               <div className="text-indigo-500 text-xs">Retailer</div>
//               <div className="font-semibold text-slate-900">
//                 ₹{template.retailer_payout}
//               </div>
//             </div>

//             <div>
//               <div className="text-indigo-500 text-xs">Other</div>
//               <div className="font-semibold text-slate-900">
//                 ₹{template.other_payout}
//               </div>
//             </div>
//           </div>

//           {/* ===== COVERAGE ===== */}
//           <div>
//             <h4 className="text-sm font-semibold text-slate-900 mb-3">
//               Coverage
//             </h4>

//             <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 space-y-2">
//               {coverageList.map((item, index) => (
//                 <div
//                   key={index}
//                   className="flex items-start gap-2 text-sm text-slate-700"
//                 >
//                   <span className="text-emerald-600 mt-0.5">✔</span>
//                   <span>{item.trim()}.</span>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* ===== EXCLUSIONS ===== */}
//           <div>
//             <h4 className="text-sm font-semibold text-slate-900 mb-3">
//               Exclusions
//             </h4>

//             <div className="bg-rose-50 border border-rose-100 rounded-2xl p-5 space-y-2">
//               {exclusionList.map((item, index) => (
//                 <div
//                   key={index}
//                   className="flex items-start gap-2 text-sm text-slate-700"
//                 >
//                   <span className="text-rose-600 mt-0.5">✖</span>
//                   <span>{item.trim()}</span>
//                 </div>
//               ))}
//             </div>
//           </div>

//         </div>
//       </div>
//     </div>
//   );
// }